import { useMemo } from "react";
import { useApiConfig, installServerErrorInterceptor, SERVER_ERROR_MESSAGE } from "common-fe";
import {
  Configuration,
  ShowsApi,
  ClassesApi,
  RidesApi,
  ScoreSheetsApi,
  TestsApi,
  PeopleApi,
  HorsesApi,
  ResultsApi,
  JudgesApi,
  DemoApi,
  CatalogApi,
  RingStateApi,
  InstanceApi,
  PresenceApi,
  ScoreboardApi,
  ReviewQueueApi,
  QuarantineApi,
  TabletImportApi,
  StationSnapshotApi,
  StationMonitorApi,
  StationsApi,
  DaySheetApi,
} from "score-common";
import { shouldRefreshBadges } from "./badgeMutationPolicy";
import { emitBadgeAffectingMutation } from "./scoreMutationEvents";

// Single place that turns the current auth session into instantiated
// score-common API clients (mirrors scribe-fe/src/api/scoreApi.js). `useApiConfig`
// (common-fe) stamps the Cognito bearer token AND the active-org `X-Org-Id`
// header (from the mounted ActiveOrgProvider) onto every request. Base URL comes
// from VITE_SCORE_API_BASE_URL.
const SCORE_API_BASE_URL = import.meta.env.VITE_SCORE_API_BASE_URL;

// Rewrite every failed API call's error.message to a generic "Server Error"
// (raw detail kept on the error for console logging) so backend codes never
// reach the UI. Installed once on the shared axios singleton at module load.
installServerErrorInterceptor();

// #3496 -- wrap a generated client so every successful call that can change a
// badge count (badgeMutationPolicy.js) tells BadgeCountsProvider to refetch.
// One place instead of a refreshBadges() at ~30 call sites.
function withBadgeRefresh(api) {
  return new Proxy(api, {
    get(target, prop) {
      // `this` must stay the real client (the generated methods read protected
      // fields off it), so every method is handed back bound to the target.
      const value = Reflect.get(target, prop, target);
      if (typeof value !== "function" || typeof prop !== "string") return value;
      if (!shouldRefreshBadges(prop)) return value.bind(target);
      return async (...args) => {
        const result = await value.apply(target, args);
        emitBadgeAffectingMutation(prop);
        return result;
      };
    },
  });
}

export function useScoreApi() {
  const buildConfig = useApiConfig();
  return useMemo(() => {
    const config = new Configuration(buildConfig(SCORE_API_BASE_URL));
    const clients = {
      shows: new ShowsApi(config),
      classes: new ClassesApi(config),
      rides: new RidesApi(config),
      scoreSheets: new ScoreSheetsApi(config),
      tests: new TestsApi(config),
      people: new PeopleApi(config),
      horses: new HorsesApi(config),
      results: new ResultsApi(config),
      judges: new JudgesApi(config),
      demo: new DemoApi(config),
      // card #1662 — GET /catalog, the global test-def catalog. Same
      // Configuration as everything else here even though the route itself
      // is org-agnostic (allowNoOrg, see apps/score-api/src/handlers/
      // catalog.js) -- an unused X-Org-Id header is harmless.
      catalog: new CatalogApi(config),
      // #1936 — GET list/single + PUT explicit set + DELETE clear, docs/score/ring-state.md.
      ringState: new RingStateApi(config),
      // #2373 — superuser foxbox instance config (list/write), consumes
      // #2372's routes. allowNoOrg on the API side; the unused X-Org-Id
      // header is harmless, same posture as `catalog` above.
      instance: new InstanceApi(config),
      // #2365 — GET /presence (#2363). Read-only here: scoreadmin never
      // heartbeats, that's scribe-fe's job (#2364).
      presence: new PresenceApi(config),
      // card #2566 — org-level sponsor library (listSponsors/putSponsor/
      // deleteSponsor, card #1635). Same client class + key name score-fe's
      // remote-control SponsorsPage already uses (apps/score-fe/src/remote/
      // api/scoreApi.js), so both apps' sponsor screens talk to the same
      // three existing routes.
      scoreboard: new ScoreboardApi(config),
      // #2813 — the generalised review queue (docs/score/scribe-resilience.md
      // gap G6): GET /review-queue (list) + POST /review-queue/:itemId/resolve
      // (generic resolve for subjects that don't need a dedicated flow).
      reviewQueue: new ReviewQueueApi(config),
      // #3356 -- GET /quarantine/:id (the held payload the review list omits) and
      // POST /quarantine/:id/apply (replay it through the normal write).
      quarantine: new QuarantineApi(config),
      // #3575 -- POST /tablet-file/import (#3571): the Review Queue's Import sheets.
      tabletImport: new TabletImportApi(config),
      // #2827 — GET /show/:showId/station-snapshot (#2929's read side): the
      // station monitor's summarized snapshot list.
      stationSnapshot: new StationSnapshotApi(config),
      // #3190 -- GET /show/:showId/station-monitor (#3189): display-ready station
      // rows (station numbers, scribe names, work segments), one call per show.
      stationMonitor: new StationMonitorApi(config),
      // #3520 -- PUT /station/:stationId/name (admin rename, GUID kept) and
      // GET /station-name/check (the 7-day same-name warning, #3508).
      stations: new StationsApi(config),
      // #3349 -- GET /daysheet/:showId/last-reconcile (the persisted FVD sync
      // outcome) and POST /daysheet/:showId/reconcile (admin re-run).
      daySheet: new DaySheetApi(config),
    };
    return Object.fromEntries(Object.entries(clients).map(([key, api]) => [key, withBadgeRefresh(api)]));
  }, [buildConfig]);
}

// JSend unwrap: score-api wraps every success as { status: "success", data }.
export function unwrap(response) {
  const body = response?.data;
  if (!body || body.status !== "success") {
    // A 2xx response carrying a JSend failure envelope — don't leak the backend
    // message/code into the UI; log it and throw the generic message.
    // eslint-disable-next-line no-console
    console.error("[score-api]", body?.data?.message || body?.code, body);
    throw new Error(SERVER_ERROR_MESSAGE);
  }
  return body.data;
}
